/**
 * One frame of the user's screen, for a diagnostic report (IR-5).
 *
 * ## The only thing that really matters here
 *
 * **The track is stopped, unconditionally, in a `finally`.** A `getDisplayMedia`
 * track that outlives its use is a live capture of somebody's screen with
 * nothing holding it and nothing showing it — the browser keeps its recording
 * indicator lit, and on Chrome the "Stop sharing" bar stays on screen with no
 * way to connect it to anything the user did. There is no error path, no early
 * return and no timeout in this file that can skip that teardown, and
 * `screen-capture-tests.js` asserts each of them individually rather than
 * asserting the happy path and trusting the rest.
 *
 * The subtle case is the LATE stream: the picker is a user gesture with no
 * deadline, so a request we gave up waiting for can still resolve minutes later
 * with a live track. `withTimeout` attaches a stopper to the original promise
 * for exactly that.
 *
 * ## A denial is a choice, not a failure
 *
 * Dismissing the picker returns `null`. No toast, no `reportError`, nothing in
 * the breadcrumb buffer that reads like a fault — the user was asked a question
 * and answered it. Only a genuinely unexpected error throws, and the caller
 * (`ui/issue-report-dialog.js`) reports that one silently and unticks the box.
 *
 * ## Two encode paths, because `ImageCapture` is Chromium-only
 *
 * `ImageCapture.grabFrame()` is the direct route and does not exist in Safari or
 * Firefox. It also REJECTS on Chromium often enough to matter — a display track
 * that has not yet produced a frame throws `InvalidStateError` — so the video
 * fallback is not only for browsers that lack the constructor; it is the path
 * taken whenever the direct one does not work out.
 *
 * Everything the module touches arrives through injected seams, so the
 * lifetime rules above are drivable in jsdom against a fake `mediaDevices`.
 */

/** How long to wait for the user to answer the picker. Generous on purpose. */
export const PICKER_TIMEOUT_MS = 60_000;
/** How long to wait for the fallback `<video>` to produce a frame. */
export const FRAME_TIMEOUT_MS = 5_000;

/** Errors that mean "the user said no", or "there is nothing to capture". */
const DECLINED = new Set([
  'NotAllowedError', // dismissed the picker, or policy forbids it
  'AbortError', // closed the picker, or navigated
  'NotFoundError', // no capturable surface
  'NotReadableError', // the OS refused (macOS screen-recording permission)
  'InvalidStateError', // not a fully active top-level document
  'SecurityError', // an insecure context, or a permissions-policy that forbids it
  'OverconstrainedError' // nothing matches what we asked for
]);

/**
 * What we ask the browser for, and — as much — what we DO NOT.
 *
 * `preferCurrentTab` pre-selects this tab in Chromium's picker, which is almost
 * always what someone reporting a builder bug wants; browsers that do not know
 * the member ignore it, because unknown dictionary members are ignored.
 *
 * **`displaySurface` is deliberately absent.** Chromium rejects
 * `getDisplayMedia` with a `TypeError` when `preferCurrentTab` is true and
 * `displaySurface` is also specified — and a `TypeError` is not in `DECLINED`,
 * so it would surface to someone reporting an unrelated bug as a second bug.
 * `displaySurface` is only a hint in the first place and `preferCurrentTab`
 * expresses the same intent more strongly, so asking for both buys nothing and
 * risks everything. Pinned by `screen-capture-tests.js`.
 */
export const DISPLAY_CONSTRAINTS = Object.freeze({
  video: true,
  audio: false,
  preferCurrentTab: true
});

export class ScreenCaptureTimeout extends Error {
  constructor(what) {
    super(`Timed out waiting for ${what}`);
    this.name = 'ScreenCaptureTimeout';
  }
}

/** Is this a build that can capture at all? Used to decide whether to OFFER the control. */
export function screenCaptureSupported(nav = globalThis.navigator) {
  return typeof nav?.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * Stop every track on a stream. Safe on null, on a stream with no tracks, and
 * on a track whose `stop` throws — because this runs in `finally` blocks whose
 * whole purpose is that nothing gets past them.
 */
export function stopStream(stream) {
  const tracks = typeof stream?.getTracks === 'function' ? stream.getTracks() : [];
  for (const track of tracks) {
    try {
      track.stop();
    } catch {
      /* a track that refuses to stop is not a reason to skip the next one */
    }
  }
}

/**
 * Reject after `ms`, and stop whatever the original promise eventually hands
 * back. Giving up on a stream is not the same as not receiving one.
 */
function withTimeout(promise, ms, what) {
  if (!Number.isFinite(ms) || ms <= 0) return promise;
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      promise.then(stopStream, () => {});
      reject(new ScreenCaptureTimeout(what));
    }, ms);
    promise.then(
      (value) => {
        if (settled) return;
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        if (settled) return;
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

function canvasToPng(canvas) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob || null), 'image/png');
  });
}

function drawToPng(source, width, height, doc) {
  const canvas = doc.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const ctx = typeof canvas.getContext === 'function' ? canvas.getContext('2d') : null;
  // A refused 2D context is a headless or memory-starved environment, not a
  // fault worth surfacing: no picture, no error.
  if (!ctx) return Promise.resolve(null);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvasToPng(canvas);
}

/** The Chromium-only direct route. Resolves null when unavailable OR unhappy. */
async function grabViaImageCapture(track, { doc, ImageCaptureCtor }) {
  if (typeof ImageCaptureCtor !== 'function') return null;
  let bitmap = null;
  try {
    bitmap = await new ImageCaptureCtor(track).grabFrame();
    return await drawToPng(bitmap, bitmap.width, bitmap.height, doc);
  } catch {
    // Documented above: a display track that has not produced a frame rejects
    // here. The caller falls through to the video path rather than giving up.
    return null;
  } finally {
    try {
      bitmap?.close?.();
    } catch {
      /* releasing a bitmap the browser already released is not an error */
    }
  }
}

/**
 * The portable route: play the stream into a detached <video> and draw one
 * frame.
 *
 * Every way this can fail resolves to `null` rather than throwing. A media
 * element that errors, or never decodes, is an environment condition — a codec,
 * a surface the OS withdrew, a machine under load — and not a defect the person
 * reporting an unrelated bug should be told about. The caller's contract is
 * "null means no picture", and this honours it.
 */
async function grabViaVideo(stream, { doc, frameTimeoutMs }) {
  const video = doc.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  try {
    await withTimeout(
      new Promise((resolve, reject) => {
        // `loadeddata` rather than `loadedmetadata`: metadata gives dimensions
        // but not necessarily a decoded frame, and drawing then yields a blank
        // canvas — the exact "20 KB uniformly white PNG" failure IR-6's
        // non-blank assertion exists to catch.
        video.addEventListener('loadeddata', () => resolve(), { once: true });
        video.addEventListener('error', () => reject(new Error('video failed')), { once: true });
        const played = video.play?.();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }),
      frameTimeoutMs,
      'a video frame'
    );
    const width = video.videoWidth || video.width || 0;
    const height = video.videoHeight || video.height || 0;
    if (!width || !height) return null;
    return await drawToPng(video, width, height, doc);
  } catch {
    return null;
  } finally {
    // The <video> is never in the document, but it holds the stream until it
    // is told otherwise.
    try {
      video.pause?.();
      video.srcObject = null;
    } catch {
      /* best-effort teardown of a node nothing else can reach */
    }
  }
}

/**
 * Capture one frame of a screen or tab the user picks.
 *
 * @param {object} [opts]
 * @param {number} [opts.pickerTimeoutMs]
 * @param {number} [opts.frameTimeoutMs]
 * @param {Navigator} [opts.nav]
 * @param {Document} [opts.doc]
 * @param {Function} [opts.ImageCaptureCtor]
 * @returns {Promise<Blob|null>} the PNG, or `null` when the user declined,
 *   the API is absent, or no frame could be drawn. Throws only on a failure
 *   that is genuinely unexpected.
 */
export async function captureScreenFrame({
  pickerTimeoutMs = PICKER_TIMEOUT_MS,
  frameTimeoutMs = FRAME_TIMEOUT_MS,
  nav = globalThis.navigator,
  doc = globalThis.document,
  ImageCaptureCtor = globalThis.ImageCapture
} = {}) {
  if (!screenCaptureSupported(nav)) return null;

  let stream = null;
  try {
    stream = await withTimeout(
      nav.mediaDevices.getDisplayMedia(DISPLAY_CONSTRAINTS),
      pickerTimeoutMs,
      'the screen picker'
    );
    const track = typeof stream?.getVideoTracks === 'function' ? stream.getVideoTracks()[0] : null;
    if (!track) return null;

    const direct = await grabViaImageCapture(track, { doc, ImageCaptureCtor });
    if (direct) return direct;
    return await grabViaVideo(stream, { doc, frameTimeoutMs });
  } catch (err) {
    if (err instanceof ScreenCaptureTimeout) return null;
    if (DECLINED.has(err?.name)) return null;
    throw err;
  } finally {
    // The one line this file exists to guarantee.
    stopStream(stream);
  }
}
